import { useAdmin } from '../ui/Shell';
import { Kpis, Table, type Row, type Tone } from '../ui/Block';
import { fmtDate } from '../lib/api';
import { DBlock, plural } from './dataShared';

export interface Timeline {
  id: string; session: string; recordingId?: string | null; kind: string;
  machineId?: string | null; app?: string | null; cpuClass?: string | null; region?: string | null; pool?: string | null;
  startKind: 'cold' | 'warm'; acceptedAt?: string | null; endedAt?: string | null; endReason?: string | null;
  events: Record<string, number>;
  downloads?: Download[];
  bootS?: number | null; waitS?: number | null; firstLineS?: number | null; firstLineAudioS?: number | null; endLagS?: number | null; audioS?: number | null;
}

export interface Download {
  name: string; file: string; files?: number; bytes: number; start_ms: number; end_ms: number;
  source: 'cdn' | 's3' | 'cdn+s3'; cdn_bytes?: number; host: string; bucket?: string; ok: boolean;
  phase: 'boot' | 'session' | 'idle';
}

const SLOW_MBPS = 150;
const SLOW_MIN = 32e6;
const SOURCE: Record<Download['source'], { label: string; tone: Tone }> = { cdn: { label: 'CDN', tone: 'info' }, s3: { label: 'S3', tone: 'mute' }, 'cdn+s3': { label: 'CDN+S3', tone: 'warn' } };
const PHASE: Record<Download['phase'], string> = { boot: 'boot', session: 'session', idle: 'idle, before it' };

function downloadRows(tl: Timeline): Row[] {
  const vm = tl.events.vm_start ?? tl.events.process_start;
  return (tl.downloads ?? []).map((d, i) => {
    const s = Math.max(d.end_ms - d.start_ms, 1) / 1000;
    const mbps = d.bytes / 1e6 / s;
    const slow = d.ok && d.bytes >= SLOW_MIN && mbps < SLOW_MBPS;
    const fg = !d.ok ? 'var(--conf-low)' : slow ? 'var(--conf-mid)' : undefined;
    const src = SOURCE[d.source] ?? { label: d.source, tone: 'mute' as Tone };
    const where = d.source === 's3' ? `${d.bucket ? `${d.bucket} @ ` : ''}${d.host}` : d.source === 'cdn+s3' && d.cdn_bytes != null
      ? `${d.host} · ${Math.round((d.cdn_bytes / d.bytes) * 100)}% from the CDN, the rest from ${d.bucket ?? 'the bucket'}` : d.host;
    return { key: `${d.name}/${d.file}/${d.start_ms}/${i}`, cells: [
      { t: d.name, sub: d.ok ? d.file : `${d.file} · failed`, fw: slow || !d.ok ? 600 : undefined, fg },
      { t: (d.bytes / 1e6).toFixed(d.bytes < 10e6 ? 2 : 0), mono: true },
      { t: s.toFixed(2), mono: true, fg },
      { t: d.ok ? mbps.toFixed(0) : '—', mono: true, fg, fw: slow ? 600 : undefined },
      { pill: src, sub: where },
      { t: vm != null ? `+${((d.start_ms - vm) / 1000).toFixed(1)}` : '—', mono: true, sub: PHASE[d.phase] ?? d.phase },
    ] };
  });
}

const EVENTS: Record<string, { t: string; step?: string; who: string; boot?: boolean; from?: string[] }> = {
  requested: { t: 'The API asks for an engine (token signed)', who: 'API' },
  vm_start: { t: 'The machine’s VM is up (kernel start)', step: 'Fly starts the machine', from: ['requested'], who: 'Fly', boot: true },
  process_start: { t: 'Server process starts', step: 'the VM boots, runs boot.sh', from: ['vm_start'], who: 'Fly', boot: true },
  listening: { t: 'Server listening', step: 'startup', from: ['process_start'], who: 'engine', boot: true },
  whisper_fetch_start: { t: 'large-v3 download starts', step: 'finds the file in the bucket, reads the manifest', from: ['listening'], who: 'engine', boot: true },
  accepted: { t: 'The session’s socket reaches the engine', step: 'the connect (the Fly proxy holds it while a stopped machine starts)', from: ['requested'], who: 'network' },
  ready_sent: { t: 'Engine tells the client it is ready', from: ['accepted'], who: 'engine' },
  first_audio: { t: 'First audio arrives', from: ['ready_sent', 'accepted'], who: 'client' },
  source_in: { t: 'The file is in hand (uploaded or fetched)', step: 'upload or fetch', from: ['accepted'], who: 'network' },
  whisper_fetch_done: { t: 'large-v3 fully downloaded', step: 'download', from: ['whisper_fetch_start'], who: 'network', boot: true },
  model_loaded: { t: 'large-v3 loaded into memory', step: 'load', from: ['whisper_fetch_done', 'listening'], who: 'CPU', boot: true },
  ready: { t: 'Engine can transcribe', step: 'diarizer, voice-activity and the gate', from: ['model_loaded'], who: 'engine', boot: true },
  engine_ready: { t: 'This session has the engine', step: 'the run lock and the pipeline', from: ['ready', 'accepted'], who: 'engine' },
  first_line: { t: 'First committed line', step: 'the first step’s lookahead and decode', from: ['engine_ready', 'first_audio', 'source_in'], who: 'CPU' },
  turbo_loaded: { t: 'Turbo loaded', from: ['ready'], who: 'engine', boot: true },
  settled: { t: 'Deferred boot work done (turbo, voices)', from: ['ready'], who: 'engine', boot: true },
  last_audio: { t: 'Last audio arrives', from: ['first_audio'], who: 'client' },
  done: { t: 'Session done', from: ['last_audio', 'first_line'], who: 'engine' },
};

const s2 = (v?: number | null) => (v == null ? '—' : `${v.toFixed(2)} s`);
const ago = (ms: number) => { const s = ms / 1000; return s < 120 ? `${s.toFixed(1)} s` : s < 7200 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`; };

function rows(tl: Timeline): { rows: Row[]; t0: number; bootNote: string | null } {
  const ev = tl.events;
  const t0 = ev.requested ?? ev.accepted;
  const own = Object.entries(ev).filter(([k]) => EVENTS[k] && (tl.startKind === 'cold' || !EVENTS[k].boot));
  own.sort((a, b) => a[1] - b[1]);
  const boot = ev.vm_start ?? ev.process_start;
  const bootNote = tl.startKind === 'warm' && boot != null && t0 != null
    ? `The engine was already up: booted ${ago(t0 - boot)} before this session${tl.bootS != null ? `, ready in ${tl.bootS.toFixed(1)} s` : ''}.`
    : null;
  const out: Row[] = own.map(([k, at]) => {
    const e = EVENTS[k];
    const starts = (e.from ?? []).map((f) => ev[f]).filter((v): v is number => v != null && v <= at);
    const step = starts.length ? (at - Math.max(...starts)) / 1000 : null;
    let stepText = step == null ? '' : `${step.toFixed(2)} s${e.step ? `: ${e.step}` : ''}`;
    if (k === 'first_line' && tl.firstLineAudioS != null) stepText += ` · the line ends at ${tl.firstLineAudioS.toFixed(1)} s of audio`;
    if (k === 'done' && tl.endLagS != null) stepText = `${tl.endLagS.toFixed(2)} s after the audio ended`;
    return { key: k, cells: [
      { t: `${((at - t0) / 1000).toFixed(2)} s`, mono: true, fw: k === 'first_line' || k === 'ready' ? 600 : undefined },
      { t: e.t, sub: stepText || undefined, fw: k === 'first_line' ? 600 : undefined },
      { t: e.who, mono: true, fg: 'var(--ink-62)' },
    ] };
  });
  return { rows: out, t0, bootNote };
}

export function StartupTimelines({ timelines }: { timelines: Timeline[] }) {
  const ctx = useAdmin();
  if (!timelines.length) {
    return <DBlock title="Start-up timeline" empty="No engine session recorded a timeline (it ran before timelines were kept, or it has not reached an engine yet)." />;
  }
  return (
    <>
      {timelines.map((tl, i) => {
        const r = rows(tl);
        const where = [tl.machineId, tl.cpuClass, tl.region, tl.pool].filter(Boolean).join(' · ');
        const timeline = (
          <DBlock key={tl.id} title={timelines.length > 1 ? `Start-up timeline · session ${i + 1} of ${timelines.length}` : 'Start-up timeline'}
            sub={<>{tl.kind} · {tl.startKind === 'cold' ? 'cold start' : 'warm engine'}{where ? ` · ${where}` : ''} · {fmtDate(tl.acceptedAt)}{tl.endReason ? ` · ended ${tl.endReason}` : tl.endedAt ? '' : ' · running'}</>}
            count={plural(r.rows.length, 'event')}>
            <Kpis min={120} items={[
              { label: tl.startKind === 'cold' ? 'COLD BOOT' : 'ENGINE READY', v: s2(tl.waitS), sub: tl.startKind === 'cold' ? `asked → can transcribe · machine ${s2(tl.bootS)}` : 'warm · asked → can transcribe' },
              { label: 'FIRST LINE', v: s2(tl.firstLineS), sub: 'asked → first committed line', fg: tl.firstLineS != null && tl.firstLineS > 20 ? 'var(--conf-mid)' : undefined },
              ...(tl.kind === 'live' ? [{ label: 'END LAG', v: s2(tl.endLagS), sub: 'audio end → done' }] : []),
            ]} />
            {r.bootNote && <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--ink-62)' }}>{r.bootNote}</div>}
            <Table mobile={ctx.mobile} minWidth={420} cols={[{ label: 'TIME', width: '76px', align: 'r' }, { label: 'EVENT · THE STEP THAT LED TO IT', width: 'minmax(240px,1fr)' }, { label: 'WHO', width: '72px' }]} rows={r.rows} />
          </DBlock>
        );
        const dl = downloadRows(tl);
        const mb = (tl.downloads ?? []).reduce((n, d) => n + d.bytes, 0) / 1e6;
        const blocks = [timeline];
        if (dl.length) blocks.push(
          <DBlock key={`${tl.id}-dl`} title={timelines.length > 1 ? `Model downloads · session ${i + 1} of ${timelines.length}` : 'Model downloads'}
            sub={<>{mb >= 1000 ? `${(mb / 1000).toFixed(2)} GB` : `${mb.toFixed(0)} MB`} · when: seconds after the VM started · under {SLOW_MBPS} MB/s is highlighted</>}
            count={plural(dl.length, 'download')}>
            <Table mobile={ctx.mobile} minWidth={520} cols={[{ label: 'MODEL · FILE', width: 'minmax(150px,1.5fr)' }, { label: 'MB', width: '52px', align: 'r' }, { label: 'S', width: '48px', align: 'r' }, { label: 'MB/S', width: '48px', align: 'r' }, { label: 'SOURCE', width: 'minmax(96px,1fr)' }, { label: 'WHEN', width: '64px', align: 'r' }]} rows={dl} />
          </DBlock>,
        );
        return blocks;
      })}
    </>
  );
}
